import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, MapPin, Heart, Loader2 } from 'lucide-react';

const SearchBar = ({ onSearch, onClear, isLoading, localAeds = [], onSuggestionSelect }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Filter local AEDs based on query
  const filterLocalAeds = useCallback((searchQuery) => {
    if (!searchQuery.trim() || !Array.isArray(localAeds)) return [];
    
    const query = searchQuery.toLowerCase();
    return localAeds
      .filter(aed => 
        (aed.name && aed.name.toLowerCase().includes(query)) ||
        (aed.address && aed.address.toLowerCase().includes(query))
      )
      .slice(0, 5) // Limit to 5 results
      .map(aed => ({
        ...aed,
        isLocal: true,
        display_name: aed.name || 'Local defibrillator',
        address: aed.address || 'Address not specified',
        type: 'aed'
      }));
  }, [localAeds]);

  // Fetch OSM suggestions when query changes
  const fetchOsmSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.trim().length < 3) return [];
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&addressdetails=1&limit=5`
      );
      const data = await response.json();
      return data.map(item => ({
        ...item,
        isLocal: false,
        type: 'place',
        display_name: item.display_name.split(',').slice(0, 3).join(',')
      }));
    } catch (error) {
      console.error('Error fetching OSM suggestions:', error);
      return [];
    }
  }, []);

  // Combined search function
  const search = useCallback(async (searchQuery) => {
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    
    try {
      const [localResults, osmResults] = await Promise.all([
        Promise.resolve(filterLocalAeds(searchQuery)),
        fetchOsmSuggestions(searchQuery)
      ]);

      // Combine and deduplicate results
      const combined = [
        ...localResults,
        ...osmResults.filter(osmItem => 
          !localResults.some(aed => 
            aed.latitude === parseFloat(osmItem.lat) && 
            aed.longitude === parseFloat(osmItem.lon)
          )
        )
      ];

      setSuggestions(combined);
    } catch (error) {
      console.error('Error during search:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [filterLocalAeds, fetchOsmSuggestions]);

  // Debounced search with ref to track the latest query
  const searchTimeoutRef = useRef(null);
  const lastQueryRef = useRef('');
  
  useEffect(() => {
    const currentQuery = query.trim();
    
    // Skip if query hasn't changed
    if (currentQuery === lastQueryRef.current) {
      return;
    }
    
    lastQueryRef.current = currentQuery;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (currentQuery.length < 2) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);
    
    searchTimeoutRef.current = setTimeout(() => {
      search(currentQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, search]);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    const currentQuery = query.trim();
    if (!currentQuery) return;
    
    // Use requestAnimationFrame to ensure state updates are batched
    requestAnimationFrame(() => {
      setShowSuggestions(false);
      if (onSearch) {
        onSearch(currentQuery);
      }
    });
  }, [query, onSearch]);

  const handleClear = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onClear) {
      onClear();
    }
  }, [onClear]);

  const handleSuggestionClick = useCallback((suggestion) => {
    if (suggestion.isLocal) {
      // For local AEDs, use the AED data directly
      setQuery(suggestion.name || 'Défibrillateur');
      if (onSuggestionSelect && suggestion.latitude && suggestion.longitude) {
        onSuggestionSelect({
          lat: suggestion.latitude,
          lon: suggestion.longitude,
          display_name: suggestion.name || 'Défibrillateur',
          address: suggestion.address
        });
      }
    } else {
      // For OSM results, use the display name
      setQuery(suggestion.display_name);
      if (onSearch) {
        onSearch(suggestion.display_name);
      }
    }
    setShowSuggestions(false);
  }, [onSuggestionSelect, onSearch]);

  const handleKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (query.trim()) {
          handleSearch(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  }, [showSuggestions, suggestions, selectedIndex, query, handleSuggestionClick, handleSearch]);

  // Close suggestions when clicking outside - using mousedown instead of click for better UX
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        // Use requestAnimationFrame to avoid race conditions with click events
        requestAnimationFrame(() => {
          setShowSuggestions(false);
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const selectedItem = suggestionsRef.current.children[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  return (
    <div className="aedapp-search" ref={searchRef}>
      <form onSubmit={handleSearch} className="w-full">
        <div className={`aedapp-search-container ${showSuggestions && suggestions.length > 0 ? 'suggestions-visible' : ''}`}>
          <Search size={18} className="aedapp-search-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              // Only show suggestions if there's text in the input
              setShowSuggestions(value.length > 0);
              // Reset selected index when query changes
              setSelectedIndex(-1);
            }}
            onFocus={() => {
              if (query.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search for an address..."
            className="aedapp-search-input"
            disabled={isLoading}
            aria-autocomplete="list"
            aria-controls="suggestions-list"
            aria-activedescendant={selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined}
          />
          {query && (
            <button 
              type="button" 
              onClick={handleClear}
              className="aedapp-search-clear"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
          <button 
            type="submit" 
            className="aedapp-search-button"
            disabled={isLoading || !query.trim()}
            aria-label={isLoading ? 'Searching...' : 'Search'}
          >
            {isLoading ? (
              <>
                <span className="loading-icon">
                  <Loader2 size={16} />
                </span>
                <span>Searching...</span>
              </>
            ) : (
              <>
                <Search size={16} className="search-icon" />
                <span>Search</span>
              </>
            )}
          </button>
          
          {(showSuggestions && (suggestions.length > 0 || isLoadingSuggestions)) && (
            <ul 
              id="suggestions-list"
              ref={suggestionsRef}
              className="suggestions-dropdown"
              role="listbox"
            >
              {isLoadingSuggestions ? (
                <li className="suggestion-item loading">
                  <div className="suggestion-text">Searching...</div>
                </li>
              ) : (
                suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.isLocal ? `local-${suggestion.id}` : `osm-${suggestion.place_id}`}
                    className={`suggestion-item ${index === selectedIndex ? 'selected' : ''} ${suggestion.isLocal ? 'local-aed' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    role="option"
                    aria-selected={index === selectedIndex}
                  >
                    {suggestion.isLocal ? (
                      <Heart size={14} className="suggestion-icon local-icon" />
                    ) : (
                      <MapPin size={14} className="suggestion-icon" />
                    )}
                    <div className="suggestion-text">
                      <div className="suggestion-main">
                        {suggestion.isLocal 
                          ? (suggestion.name || 'Defibrillator')
                          : suggestion.display_name.split(',')[0]
                        }
                      </div>
                      <div className="suggestion-details">
                        {suggestion.isLocal 
                          ? suggestion.address
                          : suggestion.display_name.split(',').slice(1, 4).join(',').trim()
                        }
                      </div>
                    </div>
                    {suggestion.isLocal && (
                      <span className="suggestion-badge">Local</span>
                    )}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </form>
    </div>
  );
};

export default SearchBar;
